/**
 * The assistant's launcher: the floating button that opens the panel while it
 * is closed (MEIS-13668). It shows what is waiting without opening anything:
 * - a red dot (`Badge`, 12 px with a 2 px paper-coloured rim) while there is news in "What's new" or an answer that
 *   finished unseen,
 * - a thin indeterminate ring around the button while a chat turn runs; the
 *   dot sits on top of it when both apply,
 * and its tooltip and accessible name say the same in words.
 *
 * Presentational only: `AgentPanel` derives the state. The ring overflows
 * visually and never takes the click; the launcher's footprint stays the
 * button's size, which `usePublishAssistantLauncherOffset` relies on.
 */
import { SmartToy as AssistantIcon } from "@mui/icons-material";
import { Badge, CircularProgress, Fab, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";

/** MUI's Fab diameters; the ring sits `RING_GAP` outside them. */
const FAB_DIAMETER = { large: 56, medium: 48 } as const;
const RING_GAP = 4;
const RING_THICKNESS = 2;
/** The unseen-activity dot's diameter in px, including its 2 px border. */
const DOT_DIAMETER = 12;

type Props = {
  size: keyof typeof FAB_DIAMETER;
  /** CSS `right` of the fixed launcher. */
  right: string;
  onOpen: () => void;
  /** Finished answers the user has not seen yet. */
  answersReady: number;
  /** Conversations with a turn still running; the ring shows while this is above 0. */
  runningCount: number;
  /** A "What's new" release the user has not seen yet. */
  news: boolean;
};

export const AgentLauncher: React.FC<Props> = ({
  size,
  right,
  onOpen,
  answersReady,
  runningCount,
  news,
}) => {
  const { t } = useTranslate(["Assistant", "Global"]);

  // The history icon's activity texts, plus the news line; with none, the
  // plain label.
  const label =
    [
      news ? t("News in the assistant", { ns: "Assistant" }) : null,
      answersReady > 0
        ? t("{count, plural, one {# answer ready} other {# answers ready}}", {
            ns: "Assistant",
            count: answersReady,
          })
        : null,
      runningCount > 0
        ? t(
            "{count, plural, one {# conversation running in the background} other {# conversations running in the background}}",
            { ns: "Assistant", count: runningCount }
          )
        : null,
    ]
      .filter((line): line is string => line !== null)
      .join(" · ") || t("Open assistant");

  const ringDiameter = FAB_DIAMETER[size] + 2 * RING_GAP;

  return (
    <Badge
      color="error"
      variant="dot"
      overlap="circular"
      invisible={!news && answersReady === 0}
      // Right edge aligned with the floating "+" of the Page layout (same
      // margin), which then docks straight above it. The badge is the fixed
      // anchor, so the dot and the ring both position against the button.
      sx={theme => ({
        position: "fixed",
        right,
        bottom: 24,
        zIndex: 1300,
        // A 12 px dot (border included) with a paper-coloured rim, so it
        // separates from the dark Fab; MUI's default 8 px is too small there.
        "& .MuiBadge-badge": {
          boxSizing: "border-box",
          width: DOT_DIAMETER,
          height: DOT_DIAMETER,
          minWidth: DOT_DIAMETER,
          padding: 0,
          borderRadius: "50%",
          border: `2px solid ${theme.palette.background.paper}`,
        },
      })}
    >
      <Tooltip title={label}>
        <Fab color="primary" size={size} aria-label={label} onClick={onOpen}>
          <AssistantIcon />
        </Fab>
      </Tooltip>
      {runningCount > 0 && (
        <CircularProgress
          size={ringDiameter}
          thickness={RING_THICKNESS}
          aria-hidden
          sx={{
            position: "absolute",
            top: -RING_GAP,
            left: -RING_GAP,
            pointerEvents: "none",
          }}
        />
      )}
    </Badge>
  );
};
